import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/tailwind.utils'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

type MultiGroupSelectGroups = Record<string, string[]>

interface MultiGroupSelectProps {
  label: string
  placeholder: string
  groups: MultiGroupSelectGroups
  activeGroup: string | null
  onGroupChange: (group: string | null) => void
  selected: string[]
  onChange: (selected: string[]) => void
  disabled?: boolean
}

const normaliseSearchValue = (value: string): string => value.trim().toLowerCase()

const getGroupDisplayName = (groupName: string): string => {
  if (groupName === 'Canterbury Hydrological Catchments - Akaroa Harbour') return 'Catchments'
  if (groupName === 'NZ General Wards') return 'Wards'
  if (groupName === 'Suburbs') return 'Suburbs'
  return groupName
}

function RiskGroupMultiGroupSelect({
  label,
  placeholder,
  groups,
  activeGroup,
  onGroupChange,
  selected,
  onChange,
  disabled = false,
}: MultiGroupSelectProps) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<string[]>(selected)
  const [search, setSearch] = useState('')

  const groupNames = useMemo(() => Object.keys(groups), [groups])

  useEffect(() => {
    if (open) {
      setPending(selected)
      setSearch('')
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const searchValue = normaliseSearchValue(search)

  const filteredItems = useMemo(() => {
    const activeItems = activeGroup !== null ? (groups[activeGroup] ?? []) : []
    return searchValue === ''
      ? activeItems
      : activeItems.filter((item) => item.toLowerCase().includes(searchValue))
  }, [activeGroup, groups, searchValue])

  const allItems = useMemo(() => Object.values(groups).flat(), [groups])
  const count = selected.length
  const pendingCount = pending.length

  const triggerLabel = useMemo(() => {
    if (count === 0) return (getGroupDisplayName(activeGroup ?? 'Whole region') || '') + ' selected'
    if (count === allItems.length) return `All ${label.toLowerCase()}`
    if (count === 1) return selected[0]
    return `${count} ${getGroupDisplayName(activeGroup ?? '').toLowerCase()} selected`
  }, [activeGroup, allItems.length, count, label, selected])

  const toggleItem = useCallback((item: string) => {
    setPending((current) =>
      current.includes(item) ? current.filter((s) => s !== item) : [...current, item],
    )
  }, [])

  const selectAllVisible = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      setPending((current) => {
        const next = [...current]

        for (const item of filteredItems) {
          if (!next.includes(item)) next.push(item)
        }

        return next
      })
    },
    [filteredItems],
  )

  const clearAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPending([])
  }

  const handleApply = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onChange(pending)
    setOpen(false)
  }

  const handleGroupChange = (groupName: string) => {
    if (groupName === activeGroup) return

    onGroupChange(groupName)
    setPending([])
    setSearch('')
  }

  const handleWholeRegion = () => {
    onGroupChange(null)
    setPending([])
    setSearch('')
  }

  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
      <label className="text-xs font-medium text-foreground">{label}</label>

      <Popover open={open} onOpenChange={(nextOpen) => !disabled && setOpen(nextOpen)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-10 w-full justify-between rounded-lg border border-border bg-background px-3 text-sm font-normal shadow-sm hover:bg-background/80"
          >
            <span className="truncate text-foreground">{triggerLabel}</span>

            {open ? (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[480px] overflow-hidden rounded-xl border bg-background p-0 shadow-lg"
        >
          <div className="flex items-center justify-between gap-4 px-4 py-4">
            <p className="text-sm font-semibold text-foreground">Filter by area</p>

            <p className="text-xs text-foreground">
              {pendingCount === 0
                ? 'Whole region'
                : `${pendingCount} area${pendingCount === 1 ? '' : 's'} selected`}
            </p>
          </div>

          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground" />

              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={
                  activeGroup !== null
                    ? `Search ${getGroupDisplayName(activeGroup).toLowerCase()}...`
                    : 'Select area type...'
                }
                className="h-10 rounded-lg border border-border pl-9 text-sm text-foreground focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>

          <div className="grid h-[300px] grid-cols-2 border-y">
            <ScrollArea className="border-r bg-neutral-100">
              <div className="py-1">
                <button
                  type="button"
                  onClick={handleWholeRegion}
                  className={cn(
                    'flex min-h-10 w-full items-center justify-between gap-2 border-l-2 border-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                    activeGroup === null
                      ? 'border-primary bg-primary/10 font-semibold text-primary'
                      : 'text-foreground',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate max-w-40" title="Whole region">
                    Whole region
                  </span>
                </button>

                {groupNames.map((groupName) => {
                  const isActive = activeGroup === groupName
                  const displayName = getGroupDisplayName(groupName)
                  const groupCount = groups[groupName]?.length ?? 0

                  return (
                    <button
                      key={groupName}
                      type="button"
                      onClick={() => handleGroupChange(groupName)}
                      className={cn(
                        'flex min-h-10 w-full items-center justify-between gap-2 border-l-2 border-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                        isActive
                          ? 'border-primary bg-primary/10 font-semibold text-primary'
                          : 'text-foreground',
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate max-w-40" title={displayName}>
                        {displayName}
                      </span>

                      <span
                        className={cn(
                          'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border px-1.5 text-[11px] font-medium',
                          isActive
                            ? 'border-primary/20 bg-primary/20 text-foreground'
                            : 'border-border bg-background text-foreground',
                        )}
                      >
                        {groupCount}
                      </span>
                    </button>
                  )
                })}
              </div>
            </ScrollArea>

            <ScrollArea>
              <div className="space-y-1 p-3">
                {filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const checked = pending.includes(item)

                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleItem(item)}
                        className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        <Checkbox
                          checked={checked}
                          aria-label={item}
                          className="pointer-events-none border border-border bg-background text-primary data-[state=checked]:border-primary data-[state=checked]:bg-primary/10 data-[state=checked]:text-primary"
                        />

                        <span className="min-w-0 flex-1 truncate text-foreground">{item}</span>
                      </button>
                    )
                  })
                ) : (
                  <div className="px-3 py-10 text-center text-sm text-foreground">
                    {activeGroup === null ? 'Whole region selected.' : 'No areas found.'}
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={clearAll}
              className="text-sm font-medium text-foreground hover:text-foreground"
              disabled={pendingCount === 0}
            >
              Clear all
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllVisible}
                className="text-sm font-semibold text-primary hover:text-primary"
              >
                Select visible
              </Button>

              <Button
                size="sm"
                onClick={handleApply}
                className="rounded-lg px-4"
                disabled={pendingCount === 0}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default RiskGroupMultiGroupSelect
